import React, { useEffect, useState } from 'react';
import { useAppStore } from '../context/AppStore';
import toast from 'react-hot-toast';
import { postsRetryAPI, postsAPI } from '../api/client';
import { Card, Badge, Button, NetworkIcon, EmptyState, ConfirmDialog, TabBar, Modal } from '../components/ui';
import { NETWORK_CONFIG } from '../api/mockData';
import { Link } from 'react-router-dom';

const STATUS_COLOR = { published: '#10b981', partial: '#f59e0b', failed: '#ef4444', scheduled: '#3b82f6' };
const STATUS_LABEL = { published: 'Publicado', partial: 'Parcial', failed: 'Fallido', scheduled: 'Programado' };

const MEDIA_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1').replace('/api/v1', '');

function MediaThumb({ post, onZoom }) {
  const [error, setError] = useState(false);
  const url = post.media_url ? `${MEDIA_BASE}${post.media_url}` : null;

  if (!url || error) {
    return (
      <div style={{
        width: '72px', height: '72px', borderRadius: 'var(--radius-md)',
        background: 'var(--bg-hover)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '28px', flexShrink: 0,
      }}>
        {post.media_type === 'video' ? '🎬' : '🖼'}
      </div>
    );
  }

  if (post.media_type === 'video') {
    return (
      <div style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
        <video
          src={url}
          style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: 'var(--radius-md)', display: 'block' }}
          muted preload="metadata"
          onError={() => setError(true)}
        />
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', borderRadius: 'var(--radius-md)', background: 'rgba(0,0,0,0.4)',
        }}>
          <span style={{ fontSize: '20px', color: '#fff' }}>▶</span>
        </div>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt="media"
      onError={() => setError(true)}
      onClick={(e) => { e.stopPropagation(); onZoom && onZoom(url); }}
      style={{
        width: '72px', height: '72px', objectFit: 'cover',
        borderRadius: 'var(--radius-md)', flexShrink: 0, display: 'block',
        cursor: onZoom ? 'zoom-in' : 'default',
      }}
    />
  );
}

function CaptionPreview({ networks }) {
  const [open, setOpen] = useState(false);
  const firstNet = networks.find(n => n.caption);
  if (!firstNet) return null;

  const caption = firstNet.caption;
  const SHORT = 100;
  const isLong = caption.length > SHORT;
  const text = isLong && !open ? caption.slice(0, SHORT) + '…' : caption;

  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--bg-elevated)',
      borderRadius: 'var(--radius-md)',
      border: '1px solid var(--border)',
    }}>
      <p style={{
        fontSize: '13px', color: 'var(--text-secondary)',
        lineHeight: 1.6, whiteSpace: 'pre-wrap',
        wordBreak: 'break-word', margin: 0,
      }}>
        {text}
      </p>
      {isLong && (
        <button
          onClick={() => setOpen(!open)}
          style={{
            marginTop: '6px', background: 'none', border: 'none', padding: 0,
            color: 'var(--accent-primary)', fontSize: '12px', fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          {open ? '▲ Ver menos' : '▼ Leer más'}
        </button>
      )}
    </div>
  );
}

function PostCard({ post, onDelete, onRetry, onReschedule }) {
  const [expanded, setExpanded] = useState(false);
  const [zoomUrl, setZoomUrl] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  const minDate = () => {
    const d = new Date(Date.now() + 60000);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const handleReschedule = async () => {
    if (!newDate) return;
    setRescheduling(true);
    try {
      await onReschedule(post.id, new Date(newDate).toISOString());
      setShowReschedule(false);
      setNewDate('');
    } finally {
      setRescheduling(false);
    }
  };

  const totalLikes    = post.networks.reduce((a, n) => a + (n.likes    || 0), 0);
  const totalComments = post.networks.reduce((a, n) => a + (n.comments || 0), 0);
  const totalReach    = post.networks.reduce((a, n) => a + (n.reach    || 0), 0);
  const totalViews    = post.networks.reduce((a, n) => a + (n.video_views || 0), 0);
  const fmt = v => v > 999 ? (v / 1000).toFixed(1) + 'K' : v;

  return (
    <>
      <div className="animate-fadeIn card-touch" style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', overflow: 'hidden', transition: 'var(--transition)',
      }}>
        {/* ── Fila principal ── */}
        <div
          onClick={() => setExpanded(!expanded)}
          style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer' }}
        >
          <MediaThumb post={post} onZoom={setZoomUrl} />

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '5px', flexWrap: 'wrap' }}>
              {post.networks.map(n => <NetworkIcon key={n.network} network={n.network} size={18} />)}
              <Badge color={STATUS_COLOR[post.status]}>{STATUS_LABEL[post.status]}</Badge>
            </div>
            <div style={{ fontSize: '12px', color: post.status === 'scheduled' ? '#3b82f6' : 'var(--text-muted)', marginBottom: '3px' }}>
              {post.status === 'scheduled' && post.scheduled_at
                ? `📅 ${new Date(post.scheduled_at + 'Z').toLocaleString('es', { dateStyle: 'medium', timeStyle: 'short', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })}`
                : new Date(post.created_at + 'Z').toLocaleString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone })
              }
            </div>
            {/* Caption preview una línea */}
            {post.networks.find(n => n.caption) && (
              <p style={{
                fontSize: '12px', color: 'var(--text-secondary)', margin: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {post.networks.find(n => n.caption)?.caption}
              </p>
            )}
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
            {[
              { icon: '❤️', val: totalLikes },
              { icon: '💬', val: totalComments },
              { icon: '👁',  val: totalReach },
              ...(totalViews > 0 ? [{ icon: '▶️', val: totalViews }] : []),
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center', minWidth: '26px' }}>
                <div style={{ fontSize: '13px', fontWeight: '700' }}>{fmt(s.val)}</div>
                <div style={{ fontSize: '10px' }}>{s.icon}</div>
              </div>
            ))}
          </div>

          <span style={{
            color: 'var(--text-muted)', fontSize: '14px', flexShrink: 0,
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }}>▾</span>
        </div>

        {/* ── Panel expandido ── */}
        {expanded && (
          <div className="animate-fadeIn" style={{
            borderTop: '1px solid var(--border)', padding: '16px',
            display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            {/* Caption completo */}
            <CaptionPreview networks={post.networks} />

            {/* Desglose por red */}
            {post.networks.map(net => {
              const cfg = NETWORK_CONFIG[net.network];
              return (
                <div key={net.network} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '12px',
                  padding: '12px 14px', borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-elevated)',
                  border: `1px solid ${net.status === 'failed' ? '#ef444430' : 'var(--border)'}`,
                }}>
                  <NetworkIcon network={net.network} size={26} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>{cfg?.name}</div>
                    {net.status === 'failed' ? (
                      <span style={{ fontSize: '12px', color: '#ef4444' }}>⚠️ {net.error_message || 'Error al publicar'}</span>
                    ) : (
                      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {net.likes        !== undefined && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>❤️ {(net.likes||0).toLocaleString('es')}</span>}
                        {net.comments     !== undefined && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>💬 {(net.comments||0).toLocaleString('es')}</span>}
                        {net.shares       !== undefined && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>🔁 {(net.shares||0).toLocaleString('es')}</span>}
                        {net.reach        !== undefined && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>👁 {(net.reach||0).toLocaleString('es')}</span>}
                        {net.video_views  > 0           && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>▶️ {net.video_views.toLocaleString('es')}</span>}
                        {net.impressions  > 0           && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>📊 {net.impressions.toLocaleString('es')} imp.</span>}
                      </div>
                    )}
                  </div>
                  <Badge color={STATUS_COLOR[net.status] || '#888'}>{STATUS_LABEL[net.status] || net.status}</Badge>
                </div>
              );
            })}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
              {(post.status === 'failed' || post.status === 'partial') && (
                <Button
                  variant="secondary" size="sm" icon="🔄"
                  onClick={() => onRetry(post.id)}
                >
                  Reintentar
                </Button>
              )}
              {post.status === 'scheduled' && (
                <Button variant="secondary" size="sm" icon="📅" onClick={() => setShowReschedule(true)}>
                  Reprogramar
                </Button>
              )}
              <Button variant="danger" size="sm" icon="🗑" onClick={() => setConfirmDelete(true)}>Eliminar</Button>
            </div>
          </div>
        )}
      </div>

      <Modal open={!!zoomUrl} onClose={() => setZoomUrl(null)} title="Vista previa" width={640}>
        {zoomUrl && <img src={zoomUrl} alt="" style={{ width: '100%', borderRadius: '12px' }} />}
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => onDelete(post.id)}
        title="Eliminar publicación"
        message="¿Estás seguro? Esto solo elimina el registro local, no borra el post en las redes sociales."
        confirmLabel="Sí, eliminar"
        danger
      />

      {showReschedule && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
        }} onClick={() => setShowReschedule(false)}>
          <div style={{
            background: 'var(--bg-card)', borderRadius: 'var(--radius-xl)',
            padding: '24px', width: '100%', maxWidth: '380px',
            border: '1px solid var(--border)', boxShadow: 'var(--shadow-xl)',
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '16px', fontWeight: '800', marginBottom: '6px' }}>📅 Reprogramar publicación</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>Selecciona la nueva fecha y hora. Debe ser posterior a la hora actual.</p>
            <input
              type="datetime-local"
              value={newDate}
              min={minDate()}
              onChange={e => setNewDate(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)', background: 'var(--bg-elevated)',
                color: 'var(--text-primary)', fontSize: '14px', fontFamily: 'var(--font-body)',
                marginBottom: '16px', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" size="sm" onClick={() => setShowReschedule(false)}>Cancelar</Button>
              <Button size="sm" onClick={handleReschedule} disabled={!newDate || rescheduling}>
                {rescheduling ? 'Guardando...' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function PostsPage() {
  const { posts, postsLoading, fetchPosts, removePost } = useAppStore();
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchPosts(); }, []);

  const handleRetry = async (postId) => {
    try {
      const { data } = await postsRetryAPI.retry(postId);
      toast.success(`Reintento completado: ${data.status}`);
      fetchPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al reintentar');
    }
  };

  const handleReschedule = async (postId, scheduledAt) => {
    try {
      await postsAPI.reschedule(postId, scheduledAt);
      toast.success('Publicación reprogramada');
      fetchPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al reprogramar');
    }
  };

  const filtered = filter === 'all' ? posts : posts.filter(p => p.status === filter);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <TabBar
          tabs={[
            { id: 'all',       label: `Todas (${posts.length})` },
            { id: 'published', label: '✓ Publicadas' },
            { id: 'scheduled', label: '📅 Programadas' },
            { id: 'partial',   label: '⚠ Parciales' },
            { id: 'failed',    label: '✕ Fallidas' },
          ]}
          active={filter}
          onChange={setFilter}
        />
        <Link to="/publish" style={{ textDecoration: 'none' }}>
          <Button icon="⚡">Nueva publicación</Button>
        </Link>
      </div>

      {postsLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton" style={{ height: '100px', borderRadius: 'var(--radius-lg)' }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="📭"
          title="No hay publicaciones"
          description="Aún no has publicado nada. Crea tu primera publicación con IA."
          action={<Link to="/publish" style={{ textDecoration: 'none' }}><Button icon="⚡">Crear publicación</Button></Link>}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filtered.map(post => <PostCard key={post.id} post={post} onDelete={removePost} onRetry={handleRetry} onReschedule={handleReschedule} />)}
        </div>
      )}
    </div>
  );
}
